import { z } from "zod";
import { fxRatePolicySchema } from "./fx.js";
import { reconcileStatusSchema } from "./admin-reconcile.js";
import { valueFormSchema } from "./value-form.js";

/* ===========================================================================
 * US-E18.F6-0199 (@UC9292–@UC9295) — ADMIN ĐỐI SOÁT ĐA KÊNH + CONFIG TỶ GIÁ.
 *
 * Đối soát cũ (US-AE5.F1-0018) so cache-vs-Σledger THEO ACCOUNT (không phân kênh). E18.F2 đã tách
 * `ledger_accounts` per `valueForm` (CASH tiền mặt | BANK | EWALLET) ⇒ surface admin này gom số dư
 * CASH của MỖI SỔ theo TỪNG KÊNH: cache (account_balances) vs Σ ledger_lines (nguồn sự thật) ⇒
 * drift TỪNG KÊNH.
 *
 * INVARIANTS:
 *  - 🔴 BẢO TOÀN: Σ 3 kênh = tổng CASH của sổ (LUÔN đủ 3 kênh, kênh vắng ⇒ 0 — không kênh nào rơi
 *    ra ngoài, không "kênh lạ" nuốt tiền). Công thức domain `sumByValueForm`/`totalOfValueForms`.
 *  - B1 no-float: mọi tiền = STRING BigInt minor. Redact theo vai (SUPPORT/READ_ONLY) ⇒ `null`
 *    (reuse `MONEY_VISIBLE_ROLES` — KHÔNG viết cơ chế redaction mới).
 *  - READ-ONLY: DTO chỉ mô tả kết quả ĐỌC. KHÔNG field mutating (không sửa ledger, không sửa cache).
 *  - Config tỷ giá: MUTATION **tách khỏi read** — sửa policy đi qua registry có sẵn
 *    `PATCH /v1/admin/system-settings/fx_rate_policy` (@Roles SUPER_ADMIN + audit). Surface này CHỈ
 *    ĐỌC (policy hiệu lực + bảng FxRate + `canEdit` cho UI gate — server 403 vẫn là chốt cuối).
 *  - `.strict()` chặn over-post / field lạ.
 * ========================================================================= */

/** Money wire = BigInt minor units (âm hợp lệ: số dư có thể âm). `null` ⇒ redact theo vai. */
const moneyMinorString = z
  .string()
  .regex(/^-?\d+$/, "money phải là số nguyên minor units (không float)");
const moneyMinorNullable = moneyMinorString.nullable();

export const ADMIN_RECONCILE_CHANNELS_MAX_LIMIT = 100;

/** Query GET /admin/reconcile/channels — lọc theo sổ/nhóm + chỉ-drift + CSV. */
export const adminReconcileChannelsQuerySchema = z
  .object({
    fundId: z.string().uuid().optional(),
    groupId: z.string().uuid().optional(),
    /** true ⇒ chỉ sổ CÓ ít nhất 1 kênh lệch (đối soát nhanh). */
    driftOnly: z.enum(["true", "false"]).optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_RECONCILE_CHANNELS_MAX_LIMIT).optional(),
    format: z.enum(["csv"]).optional(),
  })
  .strict();

export type AdminReconcileChannelsQuery = z.infer<typeof adminReconcileChannelsQuerySchema>;

/** Một KÊNH của 1 sổ: cache vs Σledger ⇒ drift. Tiền = string minor | null (redact). */
export const adminChannelReconcileRowSchema = z
  .object({
    valueForm: valueFormSchema,
    /** account_balances (cache O(1)) gom theo kênh. */
    cacheMinor: moneyMinorNullable,
    /** Σ ledger_lines signed (NGUỒN SỰ THẬT) gom theo kênh. */
    ledgerMinor: moneyMinorNullable,
    /** |cache − ledger| — luôn ≥ 0. */
    driftMinor: moneyMinorNullable,
    status: reconcileStatusSchema,
  })
  .strict();

export type AdminChannelReconcileRow = z.infer<typeof adminChannelReconcileRowSchema>;

/** Một SỔ: 3 kênh + tổng (Σ 3 kênh = tổng — bảo toàn). */
export const adminFundChannelsRowSchema = z
  .object({
    fundId: z.string().uuid(),
    fundName: z.string().nullable(),
    groupId: z.string().uuid().nullable(),
    groupName: z.string().nullable(),
    currency: z.string(),
    /** LUÔN đủ 3 kênh (kênh chưa dùng ⇒ 0) — client không phải đoán kênh vắng. */
    channels: z.array(adminChannelReconcileRowSchema).length(3),
    /** 🔴 Σ cacheMinor 3 kênh. */
    totalCacheMinor: moneyMinorNullable,
    /** 🔴 Σ ledgerMinor 3 kênh = TỔNG CASH của sổ (bảo toàn). */
    totalLedgerMinor: moneyMinorNullable,
    /** true ⇔ có ÍT NHẤT 1 kênh drift ≠ 0 (fail-loud). */
    hasDrift: z.boolean(),
  })
  .strict();

export type AdminFundChannelsRow = z.infer<typeof adminFundChannelsRowSchema>;

/** Tổng hợp đầu bảng — đếm kênh lệch + Σ|drift| (redact theo vai). */
export const adminReconcileChannelsSummarySchema = z
  .object({
    totalFunds: z.number().int().min(0),
    driftFundCount: z.number().int().min(0),
    driftChannelCount: z.number().int().min(0),
    totalDriftMinor: moneyMinorNullable,
    checkedAt: z.string(),
  })
  .strict();

export type AdminReconcileChannelsSummary = z.infer<typeof adminReconcileChannelsSummarySchema>;

export const adminReconcileChannelsResponseSchema = z
  .object({
    data: z.array(adminFundChannelsRowSchema),
    summary: adminReconcileChannelsSummarySchema,
  })
  .strict();

export type AdminReconcileChannelsResponse = z.infer<typeof adminReconcileChannelsResponseSchema>;

/* ---------------------------------------------------------------------------
 * CONFIG TỶ GIÁ (read-only surface) · @UC9294
 * ------------------------------------------------------------------------- */

/** 1 mốc tỷ giá đã khai (dấu vết) — rateScaled = ×10^8 BigInt STRING (KHÔNG float). */
export const adminFxRateRowSchema = z
  .object({
    id: z.string().uuid(),
    groupId: z.string().uuid(),
    groupName: z.string().nullable(),
    baseCurrency: z.string().length(3),
    quoteCurrency: z.string().length(3),
    rateScaled: z.string().regex(/^\d+$/),
    effectiveAt: z.string(),
    source: z.string(),
    createdAt: z.string(),
  })
  .strict();

export type AdminFxRateRow = z.infer<typeof adminFxRateRowSchema>;

/**
 * GET /admin/fx-config — ĐỌC config tỷ giá hiệu lực (registry `system_settings.fx_rate_policy`) +
 * bảng FxRate đã khai. MUTATION TÁCH RIÊNG: `PATCH /admin/system-settings/{settingKey}` (SUPER_ADMIN).
 */
export const adminFxConfigResponseSchema = z
  .object({
    /** Chính sách chốt tỷ giá hiệu lực (system-level). CONSUMER THẬT = FxConfigService/FxSpendService. */
    policy: fxRatePolicySchema,
    /** Whitelist giá trị hợp lệ (từ registry `allowed.enum`) — UI dựng select, không hardcode. */
    allowed: z.array(fxRatePolicySchema),
    /** Khoá registry để UI gọi ĐÚNG endpoint mutation (tách read). */
    settingKey: z.string(),
    /** true ⇔ vai hiện tại được PATCH (SUPER_ADMIN). UI gate — server 403 vẫn là chốt cuối (C4). */
    canEdit: z.boolean(),
    rates: z.array(adminFxRateRowSchema),
  })
  .strict();

export type AdminFxConfigResponse = z.infer<typeof adminFxConfigResponseSchema>;
